import React, { useState, useEffect } from 'react';
import NavigationSidebar from '../../components/navigation/NavigationSidebar';
import UserProfileDropdown from '../../components/navigation/UserProfileDropdown';
import NotificationCenter from '../../components/navigation/NotificationCenter';
import BreadcrumbNavigation from '../../components/navigation/BreadcrumbNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import AgencyFormModal from './components/AgencyFormModal';
import AgencyCard from './components/AgencyCard';
import AgencyFilters from './components/AgencyFilters';
import CSVImportModal from './components/CSVImportModal';
import { getAgencies, deleteAgencyAccount } from '../../services/agencyManagementService';
import { useLanguage } from '../../contexts/LanguageContext';

const AgencyManagement = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const { currentLanguage } = useLanguage();
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAgency, setEditingAgency] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [isCSVModalOpen, setIsCSVModalOpen] = useState(false);

  const labels = {
    fr: {
      title: 'Gestion des agences',
      subtitle: 'Créer et gérer les comptes clients (agences)',
      addAgency: 'Nouvelle agence',
      importCSV: 'Importer CSV',
      noAgencies: 'Aucune agence trouvée',
      noAgenciesDesc: 'Créez votre première agence cliente pour commencer.',
      deleteConfirmTitle: 'Supprimer cette agence ?',
      deleteConfirmMsg: 'Cette action est irréversible. Le compte et toutes les données associées seront supprimés.',
      cancel: 'Annuler',
      confirmDelete: 'Supprimer',
      errorLoad: 'Erreur lors du chargement des agences',
      retry: 'Réessayer',
    },
    ar: {
      title: 'إدارة الوكالات',
      subtitle: 'إنشاء وإدارة حسابات العملاء (الوكالات)',
      addAgency: 'وكالة جديدة',
      importCSV: 'استيراد CSV',
      noAgencies: 'لا توجد وكالات',
      noAgenciesDesc: 'أنشئ أول وكالة عميل للبدء.',
      deleteConfirmTitle: 'حذف هذه الوكالة؟',
      deleteConfirmMsg: 'هذا الإجراء لا يمكن التراجع عنه. سيتم حذف الحساب وجميع البيانات المرتبطة به.',
      cancel: 'إلغاء',
      confirmDelete: 'حذف',
      errorLoad: 'خطأ في تحميل الوكالات',
      retry: 'إعادة المحاولة',
    }
  };

  const l = labels?.[currentLanguage] || labels?.fr;

  const loadAgencies = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAgencies();
      setAgencies(data || []);
    } catch (err) {
      console.error('Load agencies error:', err);
      setError(l?.errorLoad);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAgencies();
  }, []);

  const handleAddAgency = () => {
    setEditingAgency(null);
    setIsModalOpen(true);
  };

  const handleEditAgency = (agency) => {
    setEditingAgency(agency);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingAgency(null);
  };

  const handleModalSuccess = () => {
    setIsModalOpen(false);
    setEditingAgency(null);
    loadAgencies();
  };

  const handleDeleteClick = (agency) => {
    setDeleteConfirm(agency);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;
    setActionLoading(true);
    try {
      await deleteAgencyAccount(deleteConfirm?.id);
      setDeleteConfirm(null);
      loadAgencies();
    } catch (err) {
      console.error('Delete error:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCSVImportSuccess = () => {
    setIsCSVModalOpen(false);
    loadAgencies();
  };

  const filteredAgencies = agencies?.filter((agency) => {
    const matchesSearch = !searchQuery ||
      agency?.agencyName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      agency?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      agency?.contactName?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesStatus = statusFilter === 'all' || agency?.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const mockUser = {
    name: currentLanguage === 'fr' ? 'Ahmed Benali' : 'أحمد بنعلي',
    role: 'super_admin',
    agency: 'OmraHajjManager',
    avatar: 'https://randomuser.me/api/portraits/men/45.jpg'
  };

  return (
    <div className="min-h-screen bg-background flex">
      <NavigationSidebar
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />

      <div className={`flex-1 flex flex-col min-h-screen transition-all duration-300 ${isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-72'}`}>
        {/* Header */}
        <header className="sticky top-0 z-40 bg-card border-b border-border shadow-warm-sm">
          <div className="flex items-center justify-between px-4 md:px-6 py-4">
            <div className="flex items-center gap-4">
              <div className="w-8 lg:hidden" />
              <BreadcrumbNavigation />
            </div>
            <div className="flex items-center gap-3">
              <NotificationCenter notifications={[]} onNotificationClick={() => {}} onMarkAsRead={() => {}} />
              <UserProfileDropdown user={mockUser} onLogout={() => {}} onLanguageChange={() => {}} />
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 md:p-6 space-y-6">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-foreground">{l?.title}</h1>
              <p className="text-muted-foreground mt-1">{l?.subtitle}</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                iconName="Upload"
                iconPosition="left"
                onClick={() => setIsCSVModalOpen(true)}
              >
                {l?.importCSV}
              </Button>
              <Button
                variant="default"
                iconName="Plus"
                iconPosition="left"
                onClick={handleAddAgency}
              >
                {l?.addAgency}
              </Button>
            </div>
          </div>

          {/* Filters */}
          <AgencyFilters
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
            currentLanguage={currentLanguage}
          />

          {/* Content */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <p className="text-muted-foreground text-sm">Chargement...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
                <Icon name="AlertCircle" size={24} className="text-destructive" />
              </div>
              <p className="text-muted-foreground">{error}</p>
              <Button variant="outline" onClick={loadAgencies}>{l?.retry}</Button>
            </div>
          ) : filteredAgencies?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                <Icon name="Building2" size={32} className="text-muted-foreground" />
              </div>
              <div className="text-center">
                <p className="font-medium text-foreground">{l?.noAgencies}</p>
                <p className="text-muted-foreground text-sm mt-1">{l?.noAgenciesDesc}</p>
              </div>
              <Button variant="default" iconName="Plus" iconPosition="left" onClick={handleAddAgency}>
                {l?.addAgency}
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredAgencies?.map((agency) => (
                <AgencyCard
                  key={agency?.id}
                  agency={agency}
                  onEdit={handleEditAgency}
                  onDelete={handleDeleteClick}
                  currentLanguage={currentLanguage}
                />
              ))}
            </div>
          )}
        </main>
      </div>

      {/* CSV Import Modal */}
      {isCSVModalOpen && (
        <CSVImportModal
          onClose={() => setIsCSVModalOpen(false)}
          onSuccess={handleCSVImportSuccess}
          currentLanguage={currentLanguage}
        />
      )}

      {/* Agency Form Modal */}
      {isModalOpen && (
        <AgencyFormModal
          agency={editingAgency}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
          currentLanguage={currentLanguage}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
          <div className="bg-card rounded-xl border border-border shadow-warm-md w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0">
                <Icon name="Trash2" size={20} className="text-destructive" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">{l?.deleteConfirmTitle}</h3>
            </div>
            <p className="text-muted-foreground text-sm mb-6">{l?.deleteConfirmMsg}</p>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setDeleteConfirm(null)} disabled={actionLoading}>
                {l?.cancel}
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteConfirm}
                loading={actionLoading}
                iconName="Trash2"
                iconPosition="left"
              >
                {l?.confirmDelete}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgencyManagement;
